/*
 * Included in sitebuilder.ts.
 *
 * We have a lot of content referencing the Twitter icon from Font Awesome 5.
 * Not only is it difficult to find and replace all that content, there is no
 * X icon in Font Awesome 5.
 *
 * This script contains an SVG version of the icon, and attempts to replace any
 * Twitter icons it finds with an SVG icon, and some supporting CSS makes sure
 * it matches in size and colour.
 *
 * It all happens at runtime so if the logo ever changed back to Twitter we could
 * just get rid of this script and pretend nothing ever happened.
 */

const templates = {
  'x': `
    <svg class="fa-x" viewBox="0 0 1200 1227" aria-hidden="true">
    <path id="p" d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" fill="currentColor"/>
    </svg>
    `.trim(),
  'x-square': `
    <svg class="fa-x-square" viewBox="0 0 512 512" aria-hidden="true">
    <path id="p" fill="currentColor" d="M 64,0 A 64,64 0 0 0 0,64 v 384 a 64,64 0 0 0 64,64 h 384 a 64,64 0 0 0 64,-64 V 64 A 64,64 0 0 0 448,0 Z m 9.427734,77.443359 H 180.96875 L 274.20703,213.13867 390.94531,77.443359 h 31.85938 L 288.35938,233.72461 h -0.008 L 434.57422,446.52539 H 327.03516 L 228.56836,303.22461 105.28906,446.52539 H 73.427734 L 214.42383,282.64062 Z m 43.341796,23.984371 113.66602,162.58789 14.28515,20.43165 v 0.008 l 97.30274,139.17578 h 48.93554 L 271.7207,253.07422 257.43555,232.64062 165.70508,101.42773 Z"></path>
    </svg>
    `.trim(),
} as const;

/** CSS classes to copy from original icon. */
const transferClasses = [
  'fa-stack-1x',
  'fa-stack-2x',
  'fa-inverse',
  'fa-fw',
  'fa-2x',
  'fa-3x',
  'fa-4x',
  'fa-5x',
  'fa-flip-vertical',
  'fa-flip-horizontal',
  'fa-rotate-90',
  'fa-rotate-180',
  'fa-rotate-270',
  'fa',
];

type Options = {
  /** If true, hides original icon and inserts SVG after it. */
  keepOriginal?: boolean;
}

const defaultOptions: Options = {
  keepOriginal: false
}

function createSvgElement(name: keyof typeof templates) {
  const el = document.createElement("div");
  el.innerHTML = templates[name];
  return el.querySelector("svg");
}

function convertIconsOfType(name: keyof typeof templates, selector: string, container: HTMLElement, options: Options) {
  container.querySelectorAll(selector).forEach((twitter: HTMLElement) => {
    if (options.keepOriginal && twitter.dataset.xCloned) {
      return;
    }
    // We need to inherit certain styles from the original
    const x = createSvgElement(name).cloneNode(true) as SVGElement;
    transferClasses.forEach((className) => {
      if (twitter.classList.contains(className)) {
        x.classList.add(className);
      }
    });
    if (options.keepOriginal) {
      twitter.dataset.xCloned = 'true';
      twitter.classList.add('hidden');
      twitter.insertAdjacentElement('afterend', x);
    } else {
      twitter.replaceWith(x);
    }
  });
}

/**
 * Looks for Twitter icons in the given container and replace them with the
 * equivalent X icon as an SVG.
 */
export function convertIcons(container: HTMLElement, options?: Options) {
  const o = Object.assign({}, defaultOptions, options);
  // SBTWO-11517 A lot of footers in the wild have used em instead of i -_- so let's handle it for them.
  convertIconsOfType('x', 'i.fa-twitter,em.fa-twitter', container, o);
  convertIconsOfType('x-square', 'i.fa-twitter-square,em.fa-twitter-square', container, o);
}
